import type { BasicStoreFragment } from '@drycut/graphql';

import { formatDateRelativeInWeek, type FormattedDurationOptions } from '@drycut/ui-format-utils';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { useStoreWaitingTimeCountDown } from './useStoreWaitingTimeCountDown';
import { WaitingTime } from './WaitingTime';

type Props = {
  store: BasicStoreFragment;
} & FormattedDurationOptions;

export function StoreWaitingTime({ store, ...options }: Props): React.ReactElement {
  const time = useStoreWaitingTimeCountDown({ store });

  // Get the earliest slot time based on the store's waiting time. The rounding trick is to ensure
  // that we always round up to the next minute so if the store opens at 10:00 we don't show 9:59
  // which can happen, depending on DateTime.now() seconds value.
  const earliestSlotTime = useMemo(
    () =>
      DateTime.now()
        .plus({ seconds: store.waitingTime + 59 }) // trick to always round up
        .startOf('minute'), // to the next minute
    [store.waitingTime],
  );

  const relativeTime = useMemo(() => {
    return formatDateRelativeInWeek({ date: earliestSlotTime, options: { steps: options.steps } });
  }, [earliestSlotTime, options.steps]);

  return <WaitingTime time={time} relativeTime={relativeTime} options={options} />;
}
